<template>
  <div>
    <div :class="$style.form">
      <labeled-input
        v-model="form.code"
        v-m-b="10"
        :error="$v.form.code.$error"
        :force-replace="projectСodeReplacementRules"
        :label="$t('project.id')"
        :maxlength="255"
        @focus="$v.form.code.$reset"
        @blur="$v.form.code.$touch"
      ></labeled-input>
      <labeled-input
        v-model="form.merchantToken"
        v-m-b="10"
        :label="$t('token.for.api')"
        data-private
        disabled
      ></labeled-input>
      <labeled-input
        v-model="form.callbackUrl"
        v-m-b="10"
        label="Callback URL"
        placeholder="https://yoursite.com/callback"
        :maxlength="255"
        :error="$v.form.callbackUrl.$error"
        @focus="$v.form.callbackUrl.$reset"
        @blur="$v.form.callbackUrl.$touch"
      ></labeled-input>
      <option-panel
        v-m-b="10"
        v-background-color:dark-blue-2
        @click="handleChangeCallbackMethod"
      >
        <span
          slot="title"
          v-text="form.callbackMethod"
        ></span>
        <span
          slot="description"
          v-text="$t('payment.technical.callback.method')"
        ></span>
        <simple-button
          slot="action"
          v-p-l="40"
          v-p-r="40"
          variant="darken"
          size="small"
        >
          {{ $t('common.change') }}
        </simple-button>
      </option-panel>
      <labeled-input
        v-model="form.successUrl"
        v-m-b="10"
        label="Success URL"
        placeholder="https://yoursite.com/callback"
        :maxlength="255"
        :error="$v.form.successUrl.$error"
        @focus="$v.form.successUrl.$reset"
        @blur="$v.form.successUrl.$touch"
      ></labeled-input>
      <labeled-input
        v-model="form.failUrl"
        label="Fail URL"
        placeholder="https://yoursite.com/callback"
        :maxlength="255"
        :error="$v.form.failUrl.$error"
        @focus="$v.form.failUrl.$reset"
        @blur="$v.form.failUrl.$touch"
      ></labeled-input>
      <cs-switcher
        v-model="form.paymentAutoRedirection"
        v-m-t="10"
        :label="$t('payment.auto.redirection.switcher.label')"
        @input="handleChangePaymentAutoRedirection"
      ></cs-switcher>
    </div>

    <div :class="$style.submit">
      <simple-button variant="primary" :disabled="!isValid" @click="handleSubmit">
        {{ $t('common.save') }}
      </simple-button>
    </div>

    <payment-auto-redirection-alert></payment-auto-redirection-alert>
    <callback-message-method-dialog
      @select-callback-method="handleSelectCallbackMethod"
    ></callback-message-method-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import {
  required as requiredValidator,
  minLength as minLengthValidator,
  maxLength as maxLengthValidator,
  url as urlValidator
} from 'vuelidate/lib/validators';

import { unescape } from 'lib/utils/string';

import LabeledInput from 'molecules/common/LabeledInput';
import SimpleButton from 'molecules/common/SimpleButton';
import OptionPanel from 'molecules/panels/OptionPanel';

import PaymentAutoRedirectionAlert from 'organisms/payment/settings/components/PaymentAutoRedirectionAlert';
import CallbackMessageMethodDialog from 'organisms/payment/settings/components/CallbackMessageMethodDialog';

import { PAYMENT_PAGE_URL_LIST } from 'definitions/url';
import { CYRILLIC_SYMBOLS, UNALLOWED_PROJECT_SYMBOLS } from 'definitions/regexp';

import { EVENT_BUS } from '@/eventBus';

const isInternalPaymentPageUrlsValidator = (value) => {
  if (PAYMENT_PAGE_URL_LIST.find(routeName => routeName === value)) {
    return false;
  }

  return true;
};

export default {
  name: 'PaymentTechnicalSettings',
  components: {
    LabeledInput,
    SimpleButton,
    OptionPanel,
    PaymentAutoRedirectionAlert,
    CallbackMessageMethodDialog,
  },
  created() {
    this.fillingData();
  },
  data() {
    return {
      form: {
        code: '',
        apiKey: '',
        callbackUrl: '',
        callbackMethod: '',
        successUrl: '',
        failUrl: '',
        paymentAutoRedirection: false,
      },
      projectСodeReplacementRules: [
        [CYRILLIC_SYMBOLS, ''],
        [UNALLOWED_PROJECT_SYMBOLS, ''],
      ],
    };
  },
  validations: {
    form: {
      code: {
        required: requiredValidator,
        minLength: minLengthValidator(1),
        maxLength: maxLengthValidator(255),
        projectCode: isInternalPaymentPageUrlsValidator,
      },
      callbackUrl: {
        url: urlValidator,
        minLength: minLengthValidator(2),
        maxLength: maxLengthValidator(255),
      },
      successUrl: {
        url: urlValidator,
        minLength: minLengthValidator(4),
        maxLength: maxLengthValidator(255),
      },
      failUrl: {
        url: urlValidator,
        minLength: minLengthValidator(4),
        maxLength: maxLengthValidator(255),
      },
    },
  },
  computed: {
    isValid() {
      if (this.$v.form.$invalid) {
        return false;
      }

      return true;
    },
    getSenderFields() {
      const { senderFields } = this.getCurrentProject;
      const { paymentAutoRedirection } = this.form;

      return {
        ...senderFields,
        email: {
          ...senderFields.email,
          selected: paymentAutoRedirection
            ? paymentAutoRedirection
            : senderFields.email.selected,
        },
      };
    },
    ...mapGetters(['getMerchant', 'getCurrentProject']),
  },
  methods: {
    fillingData() {
      const { apiKey } = this.getMerchant;
      const { code, params } = this.getCurrentProject;

      const {
        callback_url: callbackUrl,
        callback_method: callbackMethod,
        success_url: successUrl,
        fail_url: failUrl,
        payment_auto_redirection: paymentAutoRedirection,
      } = params;

      this.form = {
        code,
        merchantToken: apiKey,
        callbackUrl,
        callbackMethod: callbackMethod ?? 'POST',
        successUrl,
        failUrl,
        paymentAutoRedirection,
      };
    },
    async handleSubmit() {
      if (!this.isValid) {
        return;
      }

      try {
        await this.updateProject({
          ...this.getCurrentProject,
          code: this.form.code,
          params: {
            ...this.getCurrentProject.params,
            callback_url: unescape(this.form.callbackUrl) || null,
            callback_method: this.form.callbackMethod || 'POST',
            success_url: unescape(this.form.successUrl) || null,
            fail_url: unescape(this.form.failUrl) || null,
            payment_auto_redirection: this.form.paymentAutoRedirection,
          },
          senderFields: this.getSenderFields,
        });

        this.$alert({
          type: 'success',
          text: 'project.settings.saved',
        });
      } catch (error) {
        console.error(error);

        this.$alert({
          type: 'warning',
          text: 'project.settings.not.saved',
        });
      }
    },
    handleChangePaymentAutoRedirection(isChecked) {
      if (isChecked) {
        EVENT_BUS.$emit('EVENT_SHOW_ALERT_TO_PAYMENT_AUTO_REDIRECTION');
      }
    },
    handleChangeCallbackMethod() {
      EVENT_BUS.$emit('EVENT_SHOW_CALLBACK_MESSAGE_METHOD', this.form.callbackMethod);
    },
    handleSelectCallbackMethod(callbackMethod) {
      this.form.callbackMethod = callbackMethod;
    },
    ...mapActions('project', ['updateProject']),
  },
};
</script>

<style lang="sass" module>
  .form
    +base-sub-mount
    margin-bottom: 20px

    +respond-to('small')
      +tail-to-sub-mount
      margin-bottom: 50px

  .submit
    margin: 0 20px

    +respond-to('small')
      margin: 0 125px
</style>
